import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { formatMoney, todayYmd } from '../../utils/partnerFinance';
import { MANUAL_PAYMENT_METHODS } from '../../utils/groupBilling';
import { fieldSx } from './BillingParts';

const GroupRecordPaymentDialog = ({ open, onClose, account, currency = 'USD', onSave, saving = false, error = '' }) => {
  const { t } = useTranslation();
  const openInstallments = useMemo(() => (account?.invoices || [])
    .filter((inv) => inv.status !== 'void' && inv.type === 'invoice')
    .flatMap((inv) => inv.installments.map((i) => ({ ...i, invoiceNumber: inv.number })))
    .filter((i) => i.status !== 'void' && i.remaining > 0), [account]);

  const [form, setForm] = useState({ amount: '', method: 'bank_transfer', receivedAt: todayYmd(), reference: '', note: '', installmentId: '' });

  useEffect(() => {
    if (!open || !account) return;
    const first = openInstallments[0];
    setForm({
      amount: first ? String(first.remaining) : (account.balance > 0 ? String(account.balance) : ''),
      method: account.effectiveCollection?.method === 'external_link' ? 'external_link' : 'bank_transfer',
      receivedAt: todayYmd(),
      reference: '',
      note: '',
      installmentId: first && first.clientReportedAt ? first.id : '',
    });
  }, [open, account, openInstallments]);

  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  if (!account) return null;
  const amount = Number(form.amount);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="group-payment-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        {t('groupBilling.payment.title', { name: account.name })}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={2}>
          {openInstallments.length > 0 && (
            <FormControl size="small" sx={fieldSx}>
              <InputLabel>{t('groupBilling.payment.installment')}</InputLabel>
              <Select
                label={t('groupBilling.payment.installment')}
                value={form.installmentId}
                onChange={(e) => {
                  const inst = openInstallments.find((i) => i.id === e.target.value);
                  patch({ installmentId: e.target.value, ...(inst ? { amount: String(inst.remaining) } : {}) });
                }}
              >
                <MenuItem value="">{t('groupBilling.payment.oldestFirst')}</MenuItem>
                {openInstallments.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.invoiceNumber} · {i.label} · {i.dueDate} · {formatMoney(i.remaining, currency)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label={t('groupBilling.payment.amount', { currency })}
            type="number"
            size="small"
            sx={fieldSx}
            value={form.amount}
            onChange={(e) => patch({ amount: e.target.value })}
            inputProps={{ min: 0, step: '0.01', 'data-testid': 'group-payment-amount' }}
          />
          <FormControl size="small" sx={fieldSx}>
            <InputLabel>{t('groupBilling.payment.method')}</InputLabel>
            <Select label={t('groupBilling.payment.method')} value={form.method} onChange={(e) => patch({ method: e.target.value })}>
              {MANUAL_PAYMENT_METHODS.map((m) => (
                <MenuItem key={m} value={m}>{t(`groupBilling.paymentMethod.${m}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('groupBilling.payment.date')}
            type="date"
            size="small"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            value={form.receivedAt}
            onChange={(e) => patch({ receivedAt: e.target.value })}
          />
          <TextField label={t('groupBilling.payment.reference')} size="small" sx={fieldSx}
            value={form.reference} onChange={(e) => patch({ reference: e.target.value })} />
          <TextField label={t('groupBilling.payment.note')} size="small" sx={fieldSx} multiline minRows={2}
            value={form.note} onChange={(e) => patch({ note: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button
          sx={partnerOxbloodBtnSx}
          disabled={saving || !(amount > 0)}
          data-testid="group-payment-save"
          onClick={() => onSave({
            amount,
            method: form.method,
            receivedAt: form.receivedAt,
            reference: form.reference || null,
            note: form.note || null,
            installmentId: form.installmentId || null,
          })}
        >
          {saving ? <CircularProgress size={20} sx={{ color: DOCUMENT.ivory }} /> : t('groupBilling.payment.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupRecordPaymentDialog;
